import { useCallback, useEffect, useRef, useState } from 'react';

import {
    DOCUMENT_POLL_INTERVAL_MS,
    cancelDocumentScan,
    disposeDocumentScan,
    fetchDocumentScanImage,
    isScanFinished,
    readDocumentScan,
    scanFailureCode,
    startDocumentScan,
    type DocumentScanErrorCode,
    type DocumentScanFailureCode,
} from '../domain/documentScan';
import type { OpticalScanResult, OpticalScanSession } from '../domain/opticalScan';

/**
 * Runs one optical scan for a modal.
 *
 * A scan is a session on the workstation service, not a request, so this hook owns three things a
 * component should not have to: the poll that follows it, the abort that stops the poll when the
 * modal closes, and the disposal that tells the service to let go of the document.
 *
 * **Disposal is deliberate, not automatic.** The service keeps a completed scan - the decoded
 * document and its images - for five minutes so a page can come back for them. Every way the
 * operator ends a scan calls `reset`, which disposes of it, because holding somebody's identity
 * document for five minutes after they have finished with it is five minutes longer than
 * necessary.
 *
 * An unmount is not one of those ways. It is equally what a crash does, and a completed read is the
 * one thing in this flow that cannot be recreated - so unmount disposes of a scan that is still
 * running and leaves a finished one to the service's own retention.
 */
export type DocumentScanPhase = 'idle' | 'starting' | 'scanning' | 'finished';

/** The image kinds a scan can produce, in the order an operator should see them. */
export const PREVIEW_KINDS = ['portrait', 'visible', 'infrared'] as const;

export type ScanPreviewKind = (typeof PREVIEW_KINDS)[number];

/**
 * One image the service handed over, as something an `<img>` can render.
 *
 * A blob URL rather than a base64 string. Base64 in React state is a copy of a document image that
 * lives as long as the component does, is trivially serialised into a log line or a storage key by
 * accident, and cannot be released early. A blob URL is a handle: it is revoked the moment the
 * preview is replaced or the modal ends, and after that it renders nothing.
 */
export interface ScanPreview {
    readonly kind: ScanPreviewKind;
    readonly url: string;
}

export interface UseDocumentScan {
    phase: DocumentScanPhase;
    session: OpticalScanSession | null;
    /** Present only for a scan that completed holding a document. */
    result: OpticalScanResult | null;
    /** Why a call failed. Distinct from a scan that ran and produced nothing. */
    error: DocumentScanErrorCode | null;
    /** Why a scan that ran produced nothing. */
    failure: DocumentScanFailureCode | null;
    /**
     * Every image this scan produced, as revocable blob URLs, in {@link PREVIEW_KINDS} order.
     *
     * Only the kinds the session actually reported: a reader that produced no infrared image
     * yields no infrared preview, rather than a placeholder claiming one exists.
     */
    previews: readonly ScanPreview[];
    /**
     * The portrait's bytes, for the one image that may be stored centrally.
     *
     * Deliberately a `Blob` and not base64: the encoding happens at the upload boundary and the
     * encoded copy is released there. The visible and infrared images have no equivalent, because
     * they have nowhere to go.
     */
    portrait: Blob | null;
    start: (deviceId: string | null) => Promise<void>;
    cancel: () => Promise<void>;
    /** Ends the scan and asks the service to drop everything it is holding. */
    reset: () => Promise<void>;
}

/** What a caller can be told about, beyond what it can read from the returned state. */
export interface UseDocumentScanOptions {
    /**
     * Called once per session, the moment this hook accepts a terminal response.
     *
     * **Before React knows anything about it.** The central audit row used to be written by an
     * effect in the modal, which runs only after a successful render - so on 2026-09-14 two real
     * reads of a real document produced no row at all, because the render threw before the effect
     * could commit. An audit that a rendering defect can suppress is not an audit.
     *
     * It is handed the finished session and nothing else, and whatever it does cannot cost the
     * scan: it is called inside a guard, so a throw here is contained rather than losing a document
     * the operator has already presented.
     */
    onTerminal?: (session: OpticalScanSession) => void;
}

/** The page's own fetch, bound once so the domain module stays free of globals. */
const browserFetch = (input: string, init?: RequestInit) => window.fetch(input, init);

export function useDocumentScan(options: UseDocumentScanOptions = {}): UseDocumentScan {
    const [phase, setPhase] = useState<DocumentScanPhase>('idle');
    const [session, setSession] = useState<OpticalScanSession | null>(null);
    const [result, setResult] = useState<OpticalScanResult | null>(null);
    const [error, setError] = useState<DocumentScanErrorCode | null>(null);
    const [failure, setFailure] = useState<DocumentScanFailureCode | null>(null);
    const [previews, setPreviews] = useState<readonly ScanPreview[]>([]);
    const [portrait, setPortrait] = useState<Blob | null>(null);

    // Refs rather than state for everything the teardown path needs: an unmounting component has to
    // dispose of the session that exists *now*, not the one a stale closure captured.
    const live = useRef(true);
    const sessionId = useRef<string | null>(null);
    const poll = useRef<number | null>(null);
    const abort = useRef<AbortController | null>(null);
    /**
     * Every blob URL handed out, so teardown can revoke them without reading React state.
     *
     * A ref because unmount has to release the URLs that exist *now*: a cleanup closing over state
     * would revoke whatever the last render saw, which on a fast replace is the wrong set.
     */
    const urls = useRef<string[]>([]);
    /**
     * The latest terminal listener, so `finish` can stay stable while the caller's closure moves.
     *
     * A dependency would rebuild `finish`, and through it `observe` and `start`, on every render of
     * the component that owns this hook - which is the effect that starts a scan.
     */
    const onTerminal = useRef(options.onTerminal);
    /** The session whose ending has already been announced, so it is announced once. */
    const announced = useRef<string | null>(null);
    /**
     * The session that finished holding a document, which unmount must not destroy.
     *
     * A completed scan is the thing of value: the service holds its result for five minutes so a
     * page can come back for it, and that window is the only evidence a physical read leaves
     * behind while it is still live. The unmount cleanup used to dispose of whatever session
     * existed, which on 2026-09-14 meant a render crash unmounted the tree and deleted two real
     * completed results one second after they arrived - turning a five-minute window into a
     * one-second one, twice, with nothing to inspect afterwards.
     */
    const completed = useRef<string | null>(null);

    useEffect(() => {
        onTerminal.current = options.onTerminal;
    });

    /** Releases every preview handle. Safe to call repeatedly; the second call has nothing to do. */
    const revokePreviews = useCallback(() => {
        for (const url of urls.current) {
            URL.revokeObjectURL(url);
        }

        urls.current = [];

        if (live.current) {
            setPreviews([]);
            setPortrait(null);
        }
    }, []);

    const stopPolling = useCallback(() => {
        if (poll.current !== null) {
            window.clearInterval(poll.current);
            poll.current = null;
        }

        abort.current?.abort();
        abort.current = null;
    }, []);

    /**
     * Fetches each image the session reported and turns it into a revocable handle.
     *
     * Every URL is registered before it is shown, so a component that unmounts mid-fetch still
     * releases what was created. A kind that fails to arrive is simply absent - the others are
     * still shown, because a missing infrared image is not a reason to withhold the portrait.
     */
    const loadPreviews = useCallback(
        async (id: string, wanted: readonly { kind: ScanPreviewKind; imageId: string }[]) => {
            for (const { kind, imageId } of wanted) {
                const image = await fetchDocumentScanImage(browserFetch, id, imageId);

                if (!image.ok) {
                    continue;
                }

                if (!live.current) {
                    // Arrived after the modal went away. Nothing to render, and nothing to leak:
                    // no handle was created for it.
                    return;
                }

                const url = URL.createObjectURL(image.value);

                urls.current = [...urls.current, url];

                setPreviews(current => [...current, { kind, url }]);

                if (kind === 'portrait') {
                    setPortrait(image.value);
                }
            }
        },
        [],
    );

    const finish = useCallback(
        (finished: OpticalScanSession) => {
            stopPolling();

            // Announced before a single piece of React state moves, and therefore before anything
            // can render or fail to render. This ordering is the whole correction: the audit is a
            // consequence of the scan ending, not a consequence of the screen managing to draw it.
            if (announced.current !== finished.sessionId) {
                announced.current = finished.sessionId;

                if (finished.result !== null) {
                    // Remembered before the listener runs, so a listener that throws still cannot
                    // leave a completed result looking disposable to the unmount path.
                    completed.current = finished.sessionId;
                }

                try {
                    onTerminal.current?.(finished);
                } catch {
                    // A scan that read a person's identity document correctly is the thing of
                    // value here. An audit that could not be written must not be allowed to take
                    // it away; the caller's own reporting path is where that failure is surfaced.
                }
            }

            setPhase('finished');
            setSession(finished);
            setResult(finished.result);
            setFailure(scanFailureCode(finished.error));

            const references = finished.result?.images;

            if (references == null) {
                return;
            }

            // Only the kinds this scan actually produced. The service hands each image over exactly
            // once, so every reference is fetched at most once and never re-fetched on a re-render.
            const wanted = PREVIEW_KINDS.flatMap(kind => {
                const imageId = references[kind];

                return imageId == null ? [] : [{ kind, imageId }];
            });

            void loadPreviews(finished.sessionId, wanted);
        },
        [loadPreviews, stopPolling],
    );

    const observe = useCallback(
        (next: OpticalScanSession) => {
            if (!live.current) {
                return;
            }

            if (isScanFinished(next)) {
                finish(next);

                return;
            }

            setSession(next);
            setPhase('scanning');
        },
        [finish],
    );

    const start = useCallback(
        async (deviceId: string | null) => {
            stopPolling();

            // The session this start replaces is released before it is forgotten. Retry is the
            // ordinary way an operator reaches this line, and without the disposal below every
            // retry abandoned a session that went on holding its document images in the service's
            // scratch until the five-minute sweep found them.
            //
            // The ref is cleared *before* the call, so this is the only disposal the outgoing id
            // will ever receive: reset and the unmount cleanup both dispose of whatever
            // `sessionId.current` holds, and neither can now find it. The request is idempotent
            // regardless, but relying on that would be relying on the service to absorb a mistake
            // this hook is capable of not making.
            const superseded = sessionId.current;

            sessionId.current = null;
            completed.current = null;

            setPhase('starting');
            setError(null);
            setFailure(null);
            setResult(null);

            // The outgoing scan's images go with it. Left registered, the previous document's
            // previews would be appended to by the new scan's and revoked by neither until the
            // modal closed.
            revokePreviews();

            if (superseded !== null) {
                await disposeDocumentScan(browserFetch, superseded);
            }

            if (!live.current) {
                // The modal closed while the disposal was in flight. Nothing has been started, so
                // there is nothing else to release.
                return;
            }

            const started = await startDocumentScan(browserFetch, deviceId);

            if (!live.current) {
                // The modal closed while the start was in flight. Whatever it created is disposed of
                // rather than left for the service's own five-minute sweep to find.
                if (started.ok) {
                    void disposeDocumentScan(browserFetch, started.value.sessionId);
                }

                return;
            }

            if (!started.ok) {
                setPhase('idle');
                setError(started.code);

                return;
            }

            sessionId.current = started.value.sessionId;
            observe(started.value);

            if (isScanFinished(started.value)) {
                return;
            }

            const controller = new AbortController();

            abort.current = controller;
            poll.current = window.setInterval(() => {
                const id = sessionId.current;

                if (id === null) {
                    return;
                }

                void readDocumentScan(browserFetch, id, controller.signal).then(polled => {
                    if (polled.ok) {
                        observe(polled.value);
                    } else if (polled.code !== 'REQUEST_ABORTED' && live.current) {
                        // A poll that cannot reach the service is the end of the scan as far as this
                        // page is concerned: there is nothing left to wait for.
                        stopPolling();
                        setPhase('finished');
                        setError(polled.code);
                    }
                });
            }, DOCUMENT_POLL_INTERVAL_MS);
        },
        [observe, revokePreviews, stopPolling],
    );

    const cancel = useCallback(async () => {
        const id = sessionId.current;

        if (id === null) {
            return;
        }

        stopPolling();

        const cancelled = await cancelDocumentScan(browserFetch, id);

        if (!live.current) {
            return;
        }

        if (cancelled.ok) {
            // What the scan ended as is what actually happened, not what was asked: one that had
            // already read its document stays completed, and the operator sees that.
            observe(cancelled.value);
        } else {
            setPhase('finished');
            setError(cancelled.code);
        }
    }, [observe, stopPolling]);

    const reset = useCallback(async () => {
        const id = sessionId.current;

        stopPolling();
        sessionId.current = null;
        // An explicit end, so the protection below no longer applies: the operator has finished
        // with this document and the service should stop holding it.
        completed.current = null;

        if (live.current) {
            setPhase('idle');
            setSession(null);
            setResult(null);
            setError(null);
            setFailure(null);
        }

        // After the state reset, so a render between the two cannot show a preview whose handle
        // has already been revoked.
        revokePreviews();

        if (id !== null) {
            await disposeDocumentScan(browserFetch, id);
        }
    }, [revokePreviews, stopPolling]);

    useEffect(() => {
        live.current = true;

        return () => {
            live.current = false;

            const id = sessionId.current;

            sessionId.current = null;

            if (poll.current !== null) {
                window.clearInterval(poll.current);
                poll.current = null;
            }

            abort.current?.abort();
            abort.current = null;

            for (const url of urls.current) {
                URL.revokeObjectURL(url);
            }

            urls.current = [];

            // A scan still running is abandoned work and is disposed of: nobody is waiting for it,
            // and it is holding a reader.
            //
            // A scan that already completed is not. Unmounting says nothing about what the operator
            // decided - it is equally what a crash does, and on 2026-09-14 that is exactly what it
            // was: a render threw, React tore the tree down, and this line deleted two real
            // completed results one second after they arrived. The five-minute window the service
            // deliberately keeps exists for precisely the moment something has gone wrong, so it
            // cannot be a window that closes whenever something goes wrong. The operator's own ways
            // out - close, apply, retry - all go through `reset`, which does dispose; what is left
            // here is the involuntary case, and the service's retention sweep owns it.
            if (id !== null && completed.current !== id) {
                void disposeDocumentScan(browserFetch, id);
            }
        };
    }, []);

    return { phase, session, result, error, failure, previews, portrait, start, cancel, reset };
}
